import React, { useMemo, useState } from "react";
import { Input } from "reactstrap";
import { InputLabel } from "../components/IndividualLite/UserDetailsForm";
import ManagedAccountTncViewer from "./ManagedAccountTncViewer";

/**
 * Managed-account Terms & Conditions consent. Lists each document with an accept
 * checkbox and a "Read" action that opens the styled in-app viewer (the files are HTML
 * fragments in `public/tnc/ma/`). Accepting in the viewer ticks the box. The parent
 * gates its Next button on every box being checked (see SelfDeclarationV2.checkNext).
 *
 * Presentational only: acceptance state is owned by the parent via `acceptedDocs`
 * (array of accepted file names); toggling calls `onToggle(file, checked)`.
 */
const ManagedAccountTncConsent = ({
  documents = [],
  acceptedDocs = [],
  onToggle,
  disabled = false,
  showError = false,
  error = "",
}) => {
  const [activeDoc, setActiveDoc] = useState(null);
  const acceptedSet = useMemo(
    () => new Set(Array.isArray(acceptedDocs) ? acceptedDocs : []),
    [acceptedDocs]
  );

  if (!documents.length) return null;

  const acceptedCount = documents.reduce((count, doc) => count + (acceptedSet.has(doc.file) ? 1 : 0), 0);
  const allAccepted = acceptedCount === documents.length;

  return (
    <div className="business-v2-step-section ma-tnc" data-testid="ma-tnc-consent">
      <div className="ma-tnc__head">
        <InputLabel label="Terms & Conditions" required />
        <span className={`ma-tnc__progress ${allAccepted ? "is-complete" : ""}`} data-testid="ma-tnc-progress">
          {acceptedCount} of {documents.length} accepted
        </span>
      </div>
      <p className="ma-tnc__intro">Review each document and confirm your acceptance to continue.</p>

      <div className="ma-tnc__list">
        {documents.map((doc) => {
          const accepted = acceptedSet.has(doc.file);
          const inputId = `ma-tnc-${doc.file}`;
          return (
            <div key={doc.file} className={`ma-tnc-item ${accepted ? "is-accepted" : ""} ${disabled ? "is-disabled" : ""}`}>
              <Input
                id={inputId}
                type="checkbox"
                className="ma-tnc-item__check"
                checked={accepted}
                disabled={disabled}
                aria-label={`I accept the ${doc.label}`}
                data-testid={`ma-tnc-checkbox-${doc.file}`}
                onChange={(event) => onToggle?.(doc.file, event?.target?.checked)}
              />
              <button
                type="button"
                className="ma-tnc-item__main"
                onClick={() => setActiveDoc(doc)}
                data-testid={`ma-tnc-open-${doc.file}`}
              >
                <span className="ma-tnc-item__label">{doc.label}</span>
                <span className="ma-tnc-item__view">{accepted ? "Re-read" : "Read"}</span>
              </button>
            </div>
          );
        })}
      </div>

      {showError && error ? (
        <div className="ps-0 mt-2 ms-0 w-100 error__message left-0" data-testid="ma-tnc-error">{error}</div>
      ) : null}

      <ManagedAccountTncViewer
        doc={activeDoc}
        isOpen={Boolean(activeDoc)}
        accepted={activeDoc ? acceptedSet.has(activeDoc.file) : false}
        disabled={disabled}
        onClose={() => setActiveDoc(null)}
        onAccept={() => { if (activeDoc) onToggle?.(activeDoc.file, true); }}
      />
    </div>
  );
};

export default ManagedAccountTncConsent;
